import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode, useRef } from 'react';
import { startOfMonth, isSameMonth, startOfDay } from 'date-fns';
import { DayShift } from '@/types';
import { useDashboardTab } from '@/context/DashboardTabContext';

// Calendar scroll mode type
export type CalendarScrollMode = 'monthly' | 'infinite';

// Type for shift being placed (quick copy mode)
export interface PlacingShift {
  title: string;
  duration: number; // in minutes
  employeeId: string | null;
  employeeName: string | null;
  breaks?: any[];
  sourceShiftId: string;
  sourcePixelWidth: number; // actual pixel width from source shift
}

// UI State Context - handles calendar navigation, UI settings, real-time, and selection
interface ShiftUIContextValue {
  // Calendar state
  currentDisplayMonth: Date;
  selectedDate: Date | null;
  calendarKey: number;

  // Infinite scroll state
  scrollMode: CalendarScrollMode;
  currentScrollDate: Date | null;
  visibleDateRange: { start: Date; end: Date } | null;

  // UI settings
  hidePastDays: boolean;
  expandAllDays: boolean;
  show24Hours: boolean;
  showEvents: boolean;
  hourCellWidthPx: number;

  // Actions panel state
  isActionsPanelOpen: boolean;
  unifiedActionsPanelMode: string;

  // Real-time state
  isRealtimeConnected: boolean;
  activeUsers: Array<{userId: string; userName: string; editingShiftId?: string}>;
  usersEditingShift: (shiftId: string) => Array<{userId: string; userName: string}>;

  // Shift selection state
  selectedShiftIds: Set<string>;
  hasShiftSelection: boolean;
  selectionCount: number;

  // Quick copy / placing mode state
  isPlacingMode: boolean;
  placingShift: PlacingShift | null;

  // Resize mode state
  resizingShiftId: string | null;

  // NOTE: hoveredTimeIntervalIndex moved to ShiftHoverProvider for performance
  // See ShiftHoverProvider.tsx for the isolated hover context

  // Loading states (UI)
  refreshKey: number;

  // UI Actions - Calendar navigation
  setCurrentDisplayMonth: (date: Date) => void;
  handleNextMonth: () => void;
  handlePreviousMonth: () => void;
  handleSetToday: () => void;
  handleMonthSelect: (date: Date) => void;

  // UI Actions - Day selection
  setSelectedDate: (date: Date | null) => void;
  handleDaySelect: (date: Date) => void;
  handleCloseDayPanel: () => void;

  // UI Actions - Settings
  setHidePastDays: (value: boolean) => void;
  handleToggleHidePastDays: (value: boolean) => void;
  setExpandAllDays: (value: boolean) => void;
  handleToggleExpandAllDays: (value: boolean) => void;
  setShow24Hours: (value: boolean) => void;
  setShowEvents: (value: boolean) => void;
  setHourCellWidthPx: (value: number) => void;
  setIsActionsPanelOpen: (value: boolean) => void;
  setUnifiedActionsPanelMode: (mode: string) => void;

  // Shift selection actions
  selectShift: (shiftId: string, selected?: boolean) => void;
  selectAllShifts: () => void;
  clearShiftSelection: () => void;
  toggleShift: (shiftId: string) => void;
  isShiftSelected: (shiftId: string) => boolean;

  // Quick copy / placing mode actions
  startPlacingShift: (shift: DayShift, sourcePixelWidth: number) => void;
  cancelPlacingMode: () => void;

  // Resize mode actions
  enterResizeMode: (shiftId: string) => void;
  exitResizeMode: () => void;

  // NOTE: setHoveredTimeIntervalIndex moved to ShiftHoverProvider for performance

  // Infinite scroll actions
  setCurrentScrollDate: (date: Date) => void;
  setVisibleDateRange: (start: Date, end: Date) => void;
  scrollToDate: (date: Date, align?: 'start' | 'center' | 'end') => void;

  // Real-time actions
  setEditingShift: (shiftId: string | null) => void;

  // Combined utility actions
  forceRefresh: () => void;
}

const ShiftUIContext = createContext<ShiftUIContextValue | undefined>(undefined);

interface ShiftUIProviderProps {
  children: ReactNode;
  businessId: string | null;
  initialMonth?: Date;
  onSelectedDateChange?: (date: Date | null) => void;
  /** Calendar scroll mode - defaults to 'monthly' for backward compatibility */
  scrollMode?: CalendarScrollMode;
  /** Callback for external scroll-to-date control */
  onScrollToDate?: (date: Date, align?: 'start' | 'center' | 'end') => void;
}

const STORAGE_KEY_PREFIX = 'shift_ui_';

// Interface for persisted state
// Note: currentDisplayMonth is intentionally NOT persisted - always start with current month
interface PersistedUIState {
  hidePastDays?: boolean;
  show24Hours?: boolean;
}

export const ShiftUIProvider: React.FC<ShiftUIProviderProps> = ({
  children,
  businessId,
  initialMonth = new Date(),
  onSelectedDateChange,
  scrollMode = 'monthly',
  onScrollToDate
}) => {
  // Get navigateToShiftMonth from global context for AI-triggered navigation
  const { navigateToShiftMonth, setNavigateToShiftMonth } = useDashboardTab();

  // Create a unique storage key for each business
  const storageKey = businessId ? `${STORAGE_KEY_PREFIX}${businessId}` : `${STORAGE_KEY_PREFIX}default`;

  // Load persisted state from localStorage
  const loadPersistedState = useCallback((): PersistedUIState => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading shift UI state from localStorage:', error);
    }
    return {};
  }, [storageKey]);

  // Initialize persisted state once
  const persistedState = loadPersistedState();

  // Calendar state - always start with current month (don't persist month selection)
  const [currentDisplayMonth, setCurrentDisplayMonth] = useState<Date>(initialMonth);
  const [selectedDate, setSelectedDateState] = useState<Date | null>(null);
  const [calendarKey, setCalendarKey] = useState(0);

  // Infinite scroll state
  const [currentScrollDate, setCurrentScrollDateState] = useState<Date | null>(
    scrollMode === 'infinite' ? new Date() : null
  );
  const [visibleDateRange, setVisibleDateRangeState] = useState<{ start: Date; end: Date } | null>(null);

  // Ref for scroll-to-date callback (set by calendar component)
  const scrollToDateCallbackRef = useRef<((date: Date, align?: 'start' | 'center' | 'end') => void) | null>(null);

  // UI settings - load from localStorage if available
  const [hidePastDays, setHidePastDays] = useState(persistedState.hidePastDays ?? false);
  const [expandAllDays, setExpandAllDays] = useState(false);
  const [show24Hours, setShow24Hours] = useState(persistedState.show24Hours ?? false);
  const [showEvents, setShowEvents] = useState(true);
  const [hourCellWidthPx, setHourCellWidthPx] = useState(60);

  // Actions panel state
  const [isActionsPanelOpen, setIsActionsPanelOpen] = useState(false);
  const [unifiedActionsPanelMode, setUnifiedActionsPanelMode] = useState('employees');

  // Real-time state (placeholder implementation)
  const [isRealtimeConnected] = useState(false);
  const [activeUsers] = useState<Array<{userId: string; userName: string; editingShiftId?: string}>>([]);

  // Shift selection state
  const [selectedShiftIds, setSelectedShiftIds] = useState<Set<string>>(new Set());

  // Quick copy / placing mode state
  const [isPlacingMode, setIsPlacingMode] = useState(false);
  const [placingShift, setPlacingShift] = useState<PlacingShift | null>(null);

  // Resize mode state
  const [resizingShiftId, setResizingShiftId] = useState<string | null>(null);

  // NOTE: hoveredTimeIntervalIndex state moved to ShiftHoverProvider for performance

  // Loading states
  const [refreshKey, setRefreshKey] = useState(0);

  // Save persisted state to localStorage whenever it changes
  // Note: currentDisplayMonth is intentionally NOT persisted - always start with current month
  useEffect(() => {
    try {
      const stateToSave: PersistedUIState = {
        hidePastDays,
        show24Hours,
      };
      localStorage.setItem(storageKey, JSON.stringify(stateToSave));
    } catch (error) {
      console.error('Error saving shift UI state to localStorage:', error);
    }
  }, [hidePastDays, show24Hours, storageKey]);

  // Sync calendar month with navigateToShiftMonth from global context (AI-triggered navigation)
  useEffect(() => {
    if (navigateToShiftMonth && !isSameMonth(navigateToShiftMonth, currentDisplayMonth)) {
      setCurrentDisplayMonth(startOfMonth(navigateToShiftMonth));
      // Clear the navigation trigger after handling it
      setNavigateToShiftMonth(null);
    }
  }, [navigateToShiftMonth, currentDisplayMonth, setNavigateToShiftMonth]);

  // Calendar navigation actions
  const handleNextMonth = useCallback(() => {
    setCurrentDisplayMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }, []);

  const handlePreviousMonth = useCallback(() => {
    setCurrentDisplayMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }, []);

  const handleSetToday = useCallback(() => {
    setCurrentDisplayMonth(new Date());
  }, []);

  const handleMonthSelect = useCallback((date: Date) => {
    setCurrentDisplayMonth(date);
  }, []);

  // Day selection actions
  const setSelectedDate = useCallback((date: Date | null) => {
    setSelectedDateState(date);
    onSelectedDateChange?.(date);
  }, [onSelectedDateChange]);

  const handleDaySelect = useCallback((date: Date) => {
    setSelectedDate(date);
  }, [setSelectedDate]);

  const handleCloseDayPanel = useCallback(() => {
    setSelectedDate(null);
  }, [setSelectedDate]);

  // UI settings actions
  const handleToggleHidePastDays = useCallback((value: boolean) => {
    setHidePastDays(value);
  }, []);

  const handleToggleExpandAllDays = useCallback((value: boolean) => {
    setExpandAllDays(value);
  }, []);

  // Shift selection actions
  const selectShift = useCallback((shiftId: string, selected?: boolean) => {
    setSelectedShiftIds(prev => {
      const newSet = new Set(prev);
      const shouldSelect = selected !== undefined ? selected : !prev.has(shiftId);

      if (shouldSelect) {
        newSet.add(shiftId);
      } else {
        newSet.delete(shiftId);
      }

      return newSet;
    });
  }, []);

  const selectAllShifts = useCallback(() => {
    // This would need access to shifts data - will be connected when providers are composed
    console.log('Select all shifts - needs data provider connection');
  }, []);

  const clearShiftSelection = useCallback(() => {
    setSelectedShiftIds(new Set());
  }, []);

  const toggleShift = useCallback((shiftId: string) => {
    selectShift(shiftId);
  }, [selectShift]);

  const isShiftSelected = useCallback((shiftId: string) => {
    return selectedShiftIds.has(shiftId);
  }, [selectedShiftIds]);

  // Quick copy / placing mode actions
  const startPlacingShift = useCallback((shift: DayShift, sourcePixelWidth: number) => {
    const duration = Math.round((shift.endTime.getTime() - shift.startTime.getTime()) / (1000 * 60));
    setPlacingShift({
      title: shift.title || 'Untitled',
      duration,
      employeeId: shift.employeeId || null,
      employeeName: shift.employeeName || null,
      breaks: shift.breaks,
      sourceShiftId: shift.id,
      sourcePixelWidth,
    });
    setIsPlacingMode(true);
  }, []);

  const cancelPlacingMode = useCallback(() => {
    setIsPlacingMode(false);
    setPlacingShift(null);
  }, []);

  // Cancel placing mode on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isPlacingMode) {
        cancelPlacingMode();
      }
    };

    if (isPlacingMode) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isPlacingMode, cancelPlacingMode]);

  // Resize mode actions
  const enterResizeMode = useCallback((shiftId: string) => {
    setResizingShiftId(shiftId);
  }, []);

  const exitResizeMode = useCallback(() => {
    setResizingShiftId(null);
  }, []);

  // Cancel resize mode on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && resizingShiftId) {
        exitResizeMode();
      }
    };

    if (resizingShiftId) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [resizingShiftId, exitResizeMode]);

  // Infinite scroll actions
  const setCurrentScrollDate = useCallback((date: Date) => {
    setCurrentScrollDateState(startOfDay(date));
  }, []);

  const setVisibleDateRange = useCallback((start: Date, end: Date) => {
    setVisibleDateRangeState({ start: startOfDay(start), end: startOfDay(end) });
    // Also update currentScrollDate to the start of visible range
    setCurrentScrollDateState(startOfDay(start));
  }, []);

  // Scroll to a specific date - uses callback from calendar component
  const scrollToDate = useCallback((date: Date, align: 'start' | 'center' | 'end' = 'center') => {
    // First, try the external callback if provided
    if (onScrollToDate) {
      onScrollToDate(date, align);
      return;
    }

    // Otherwise, use the internal callback set by the calendar component
    if (scrollToDateCallbackRef.current) {
      scrollToDateCallbackRef.current(date, align);
    }
  }, [onScrollToDate]);

  // Real-time actions (placeholder)
  const usersEditingShift = useCallback((shiftId: string) => {
    return activeUsers.filter(user => user.editingShiftId === shiftId);
  }, [activeUsers]);

  const setEditingShift = useCallback((shiftId: string | null) => {
    console.log('Set editing shift:', shiftId);
  }, []);

  // Utility actions
  const forceRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Computed values
  const hasShiftSelection = selectedShiftIds.size > 0;
  const selectionCount = selectedShiftIds.size;

  // Memoize context value to prevent unnecessary rerenders
  // Only recreate when actual state or callbacks change
  const value: ShiftUIContextValue = useMemo(() => ({
    // State
    currentDisplayMonth,
    selectedDate,
    calendarKey,

    // Infinite scroll state
    scrollMode,
    currentScrollDate,
    visibleDateRange,

    // UI settings
    hidePastDays,
    expandAllDays,
    show24Hours,
    showEvents,
    hourCellWidthPx,
    isActionsPanelOpen,
    unifiedActionsPanelMode,
    isRealtimeConnected,
    activeUsers,
    usersEditingShift,
    selectedShiftIds,
    hasShiftSelection,
    selectionCount,
    isPlacingMode,
    placingShift,
    resizingShiftId,
    refreshKey,

    // Actions
    setCurrentDisplayMonth,
    handleNextMonth,
    handlePreviousMonth,
    handleSetToday,
    handleMonthSelect,
    setSelectedDate,
    handleDaySelect,
    handleCloseDayPanel,
    setHidePastDays,
    handleToggleHidePastDays,
    setExpandAllDays,
    handleToggleExpandAllDays,
    setShow24Hours,
    setShowEvents,
    setHourCellWidthPx,
    setIsActionsPanelOpen,
    setUnifiedActionsPanelMode,
    selectShift,
    selectAllShifts,
    clearShiftSelection,
    toggleShift,
    isShiftSelected,
    startPlacingShift,
    cancelPlacingMode,
    enterResizeMode,
    exitResizeMode,

    // Infinite scroll actions
    setCurrentScrollDate,
    setVisibleDateRange,
    scrollToDate,

    setEditingShift,
    forceRefresh,
  }), [
    // All state dependencies
    currentDisplayMonth,
    selectedDate,
    calendarKey,
    // Infinite scroll state
    scrollMode,
    currentScrollDate,
    visibleDateRange,
    // UI settings
    hidePastDays,
    expandAllDays,
    show24Hours,
    showEvents,
    hourCellWidthPx,
    isActionsPanelOpen,
    unifiedActionsPanelMode,
    isRealtimeConnected,
    activeUsers,
    usersEditingShift,
    selectedShiftIds,
    hasShiftSelection,
    selectionCount,
    isPlacingMode,
    placingShift,
    resizingShiftId,
    refreshKey,
    // All callbacks are already memoized with useCallback
    setCurrentDisplayMonth,
    handleNextMonth,
    handlePreviousMonth,
    handleSetToday,
    handleMonthSelect,
    setSelectedDate,
    handleDaySelect,
    handleCloseDayPanel,
    setHidePastDays,
    handleToggleHidePastDays,
    setExpandAllDays,
    handleToggleExpandAllDays,
    setShow24Hours,
    setShowEvents,
    setHourCellWidthPx,
    setIsActionsPanelOpen,
    setUnifiedActionsPanelMode,
    selectShift,
    selectAllShifts,
    clearShiftSelection,
    toggleShift,
    isShiftSelected,
    startPlacingShift,
    cancelPlacingMode,
    enterResizeMode,
    exitResizeMode,
    // Infinite scroll actions
    setCurrentScrollDate,
    setVisibleDateRange,
    scrollToDate,
    setEditingShift,
    forceRefresh,
  ]);

  return <ShiftUIContext.Provider value={value}>{children}</ShiftUIContext.Provider>;
};

export const useShiftUI = (): ShiftUIContextValue => {
  const context = useContext(ShiftUIContext);
  if (context === undefined) {
    throw new Error('useShiftUI must be used within a ShiftUIProvider');
  }
  return context;
};